/**
 * dataService.js
 * ─────────────────────────────────────────────────────────────────
 * Client-side data access. Data is loaded from and persisted to Postgres
 * through the Vercel /api/data function.
 *
 * ── SCHEMA ──────────────────────────────────────────────────────
 * accounts[]  { id, name, platform }
 * records[]   { id, accountId, date (YYYY-MM-DD), fbSpend, consumables, revenue }
 *
 * ── FORMULAS ────────────────────────────────────────────────────
 *   Профит = Доход  − Расход ФБ − Расходники
 *   ROI    = Профит / (Расход ФБ + Расходники) × 100
 */

let database = {
  users: [], accounts: [], records: [], expenses: [], fbAccounts: [],
  spendRecords: [], orders: [], conversions: [],
};

// ── Internal helpers ─────────────────────────────────────────────

function getDB() {
  return database;
}

export async function initializeData() {
  const response = await fetch('/api/data');
  if (!response.ok) throw new Error(`Не удалось загрузить данные (${response.status})`);
  database = await response.json();
  return database;
}

function saveDB(db, collection) {
  database = db;
  fetch('/api/data', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ collection, rows: db[collection] || [] }),
  }).then((response) => {
    if (!response.ok) throw new Error(`Не удалось сохранить ${collection}`);
  }).catch((error) => console.error(error));
}

export async function authenticate(email, password) {
  const response = await fetch('/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error('Ошибка авторизации');
  return response.json();
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

function campaignPartKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\.{3,}/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function cleanCampaignPart(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/\.{3,}$/, '...');
}

function parseCampaignName(campaign) {
  const text = String(campaign || '').trim();
  const parsed = text.match(/^(.*?)_\[(.*?)\]\s*->\s*(.*)$/);

  if (!parsed) {
    return {
      owner: '',
      source: '',
      target: text,
      raw: text,
    };
  }

  return {
    owner: parsed[1],
    source: parsed[2],
    target: parsed[3],
    raw: text,
  };
}

function diceSimilarity(a, b) {
  const left = campaignPartKey(a);
  const right = campaignPartKey(b);

  if (left === right) return 1;
  if (!left || !right) return 0;
  if (
    left.length >= 6 &&
    right.length >= 6 &&
    (left.startsWith(right) || right.startsWith(left))
  ) {
    return 1;
  }
  if (left.length < 2 || right.length < 2) return 0;

  const counts = new Map();
  for (let i = 0; i < left.length - 1; i += 1) {
    const gram = left.slice(i, i + 2);
    counts.set(gram, (counts.get(gram) || 0) + 1);
  }

  let matches = 0;
  for (let i = 0; i < right.length - 1; i += 1) {
    const gram = right.slice(i, i + 2);
    const count = counts.get(gram) || 0;
    if (count > 0) {
      matches += 1;
      counts.set(gram, count - 1);
    }
  }

  return (2 * matches) / (left.length + right.length - 2);
}

function areSimilarCampaigns(a, b) {
  const left = parseCampaignName(a);
  const right = parseCampaignName(b);

  if (!left.raw || !right.raw) return false;
  if (!left.source || !right.source) return diceSimilarity(left.target, right.target) >= 0.94;

  const sourceScore = diceSimilarity(left.source, right.source);
  const targetScore = diceSimilarity(left.target, right.target);

  return (
    (sourceScore >= 0.84 && targetScore >= 0.84) ||
    (sourceScore >= 0.72 && targetScore >= 0.94)
  );
}

function bestByCount(values, counts) {
  return [...values].sort((a, b) => {
    const countDiff = (counts.get(b.raw) || 0) - (counts.get(a.raw) || 0);
    if (countDiff !== 0) return countDiff;
    return b.raw.length - a.raw.length;
  })[0];
}

function bestCampaignTarget(values) {
  return [...values].sort((a, b) => {
    const aComplete = a.target.includes('...') ? 0 : 1;
    const bComplete = b.target.includes('...') ? 0 : 1;
    if (aComplete !== bComplete) return bComplete - aComplete;
    return campaignPartKey(b.target).length - campaignPartKey(a.target).length;
  })[0];
}

function buildCampaignCanonicalMap(conversions) {
  const counts = new Map();
  (conversions || []).forEach((c) => {
    const campaign = String(c.campaign || '').trim();
    if (campaign) counts.set(campaign, (counts.get(campaign) || 0) + 1);
  });

  const names = [...counts.keys()];
  const parent = new Map(names.map((name) => [name, name]));

  const find = (name) => {
    const root = parent.get(name);
    if (root === name) return root;
    const next = find(root);
    parent.set(name, next);
    return next;
  };

  const union = (a, b) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent.set(rootB, rootA);
  };

  for (let i = 0; i < names.length; i += 1) {
    for (let j = i + 1; j < names.length; j += 1) {
      if (areSimilarCampaigns(names[i], names[j])) union(names[i], names[j]);
    }
  }

  const groups = new Map();
  names.forEach((name) => {
    const root = find(name);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(parseCampaignName(name));
  });

  const canonicalByRaw = new Map();
  groups.forEach((group) => {
    const base = bestByCount(group, counts);
    const target = bestCampaignTarget(group);
    const canonical = base.source
      ? `${base.owner}_[${cleanCampaignPart(base.source)}] -> ${cleanCampaignPart(target.target)}`
      : cleanCampaignPart(base.raw);

    group.forEach((item) => canonicalByRaw.set(item.raw, canonical));
  });

  return canonicalByRaw;
}

// ── Date utilities ───────────────────────────────────────────────

/** Date → "YYYY-MM-DD" (local timezone, not UTC) */
export function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** "YYYY-MM-DD" → "DD.MM.YYYY" */
export function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

/** Number → "13 344.68$"  (trailing zeros stripped) */
export function fmtMoney(n) {
  const s = parseFloat(n.toFixed(2)).toString();
  const [int, dec] = s.split('.');
  const intFmt = int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0'); // non-breaking space
  return dec ? `${intFmt}.${dec}$` : `${intFmt}$`;
}

/** Number → "43.82%" */
export function fmtPct(n) {
  return `${n.toFixed(2)}%`;
}

// ── Preset date ranges ───────────────────────────────────────────

export const PRESETS = [
  { key: 'today',     label: 'Сегодня' },
  { key: 'yesterday', label: 'Вчера' },
  { key: '7days',     label: 'Последние 7 дней' },
  { key: '14days',    label: 'Последние 14 дней' },
  { key: '30days',    label: 'Последние 30 дней' },
  { key: 'thisMonth', label: 'Этот месяц' },
  { key: 'previousMonth', label: 'Предыдущий месяц' },
];

/** Returns { from: "YYYY-MM-DD", to: "YYYY-MM-DD" } */
export function getPresetRange(key) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const to = toISO(today);

  switch (key) {
    case 'today':
      return { from: to, to };

    case 'yesterday': {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      const s = toISO(d);
      return { from: s, to: s };
    }

    case '7days': {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      return { from: toISO(d), to };
    }

    case '14days': {
      const d = new Date(today);
      d.setDate(d.getDate() - 13);
      return { from: toISO(d), to };
    }

    case '30days': {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      return { from: toISO(d), to };
    }

    case 'thisMonth': {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: toISO(d), to };
    }

    case 'previousMonth': {
      const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: toISO(firstDay), to: toISO(lastDay) };
    }

    default:
      return { from: to, to };
  }
}

// ── Core formula ─────────────────────────────────────────────────

/**
 * Aggregate an array of records into { fbSpend, consumables, revenue, profit, roi }.
 *
 *   profit = revenue − fbSpend − consumables
 *   roi    = profit  / (fbSpend + consumables) × 100
 */
export function calcStats(records) {
  const fbSpend     = records.reduce((s, r) => s + (r.fbSpend     || 0), 0);
  const consumables = records.reduce((s, r) => s + (r.consumables || 0), 0);
  const revenue     = records.reduce((s, r) => s + (r.revenue     || 0), 0);

  const profit = revenue - fbSpend - consumables;
  const cost   = fbSpend + consumables;
  const roi    = cost > 0 ? (profit / cost) * 100 : 0;

  return {
    fbSpend:      round2(fbSpend),
    consumables:  round2(consumables),
    revenue:      round2(revenue),
    profit:       round2(profit),
    roi:          round2(roi),
  };
}

// ── Records ──────────────────────────────────────────────────────

export function getAllRecords() {
  return getDB().records;
}

export function getRecordsByRange(from, to) {
  return getDB().records.filter(r => r.date >= from && r.date <= to);
}

/**
 * Add a daily record.
 * @param {{ accountId, date, fbSpend, consumables, revenue }} data
 */
export function addRecord(data) {
  const db  = getDB();
  const rec = { id: `rec_${Date.now()}`, ...data };
  db.records.push(rec);
  db.records.sort((a, b) => a.date.localeCompare(b.date));
  saveDB(db, 'records');
  return rec;
}

export function updateRecord(id, updates) {
  const db = getDB();
  const i  = db.records.findIndex(r => r.id === id);
  if (i !== -1) {
    db.records[i] = { ...db.records[i], ...updates };
    saveDB(db, 'records');
    return db.records[i];
  }
  return null;
}

export function deleteRecord(id) {
  const db = getDB();
  db.records = db.records.filter(r => r.id !== id);
  saveDB(db, 'records');
}

// ── Accounts ─────────────────────────────────────────────────────

export function getAccounts() {
  return getDB().accounts;
}

/**
 * Per-account aggregated stats for the given date range.
 * Returns array of { id, name, platform, fbSpend, consumables, revenue, profit, roi }.
 */
export function getAccountStats(from, to) {
  const db = getDB();
  const primaryName = (db.users || []).find((user) => user.name)?.name || 'KirillLikholetov';
  const expenseOwner = (expense) => String(expense.name || '').trim() || primaryName;

  /* ── gather per-user expenses ── */
  const periodExpenses = (db.expenses || []).filter(
    (e) => e.date >= from && e.date <= to,
  );
  /* ── gather per-user revenue (sale conversions) ── */
  const periodSales = (db.conversions || []).filter((c) => {
    const day = c.conversionTime ? c.conversionTime.slice(0, 10) : '';
    return day >= from && day <= to && c.origStatus === 'sale';
  });

  /* Empty owner names belong to the signed-in/default user, not a separate row. */
  const names = new Set(
    (db.users || [])
      .map((u) => String(u.name || '').trim())
      .filter(Boolean)
      .concat(periodExpenses.map(expenseOwner)),
  );
  if (names.size === 0) return [];

  return [...names].map((name, idx) => {
    const fbSpend = round2(
      periodExpenses
        .filter((e) => expenseOwner(e) === name)
        .reduce((s, e) => s + (Number(e.amount) || 0), 0),
    );
    const revenue = round2(
      periodSales.reduce((s, c) => s + (Number(c.revenue) || 0), 0),
    );
    const profit = round2(revenue - fbSpend);
    const roi    = fbSpend > 0 ? round2((profit / fbSpend) * 100) : 0;

    return {
      rowNum: idx + 1,
      id:     `user_${idx + 1}`,
      name,
      fbSpend,
      revenue,
      profit,
      roi,
    };
  });
}

// ── Orders ────────────────────────────────────────────────────────

export function getOrdersByRange(from, to) {
  const db = getDB();
  return (db.orders || [])
    .filter((o) => o.createdAt >= from && o.createdAt <= to)
    .sort((a, b) => b.num - a.num);
}

/** Sum of order amounts for the given date range */
export function getOrdersTotal(from, to) {
  const db = getDB();
  return round2(
    (db.orders || [])
      .filter((o) => o.createdAt >= from && o.createdAt <= to)
      .reduce((s, o) => s + (Number(o.amount) || 0), 0)
  );
}

export function getAllOrders() {
  const db = getDB();
  return (db.orders || []).slice().sort((a, b) => b.num - a.num);
}

export function addOrder(title, qty = 1, amount = 0, author = 'KirillLikholetov') {
  const db = getDB();
  if (!db.orders) db.orders = [];
  const maxNum = db.orders.reduce((m, o) => Math.max(m, o.num || 0), 0);
  const num    = maxNum + 1;
  const today  = new Date().toISOString().slice(0, 10);
  const order  = {
    id:          `ord_${num}`,
    num,
    author,
    createdAt:   today,
    completedAt: null,
    title,
    executor:    '',
    qty,
    amount,
    status:      'pending',
    deadline:    null,
  };
  db.orders.push(order);
  saveDB(db, 'orders');
  return order;
}

// ── Spend Model ─────────────────────────────────────────────────

export function getAllSpendRecords() {
  const db = getDB();
  const list = (db.spendRecords || []).slice();
  list.sort((a, b) => b.date.localeCompare(a.date));
  return list;
}

export function addSpendRecord(data) {
  const db = getDB();
  if (!db.spendRecords) db.spendRecords = [];
  const rec = { id: `spend_${Date.now()}`, ...data };
  db.spendRecords.push(rec);
  saveDB(db, 'spendRecords');
  return rec;
}

export function updateSpendRecord(id, updates) {
  const db = getDB();
  if (!db.spendRecords) return null;
  const i = db.spendRecords.findIndex((r) => r.id === id);
  if (i !== -1) {
    db.spendRecords[i] = { ...db.spendRecords[i], ...updates };
    saveDB(db, 'spendRecords');
    return db.spendRecords[i];
  }
  return null;
}

export function deleteSpendRecord(id) {
  const db = getDB();
  if (!db.spendRecords) return;
  db.spendRecords = db.spendRecords.filter((r) => r.id !== id);
  saveDB(db, 'spendRecords');
}

// ── FB Accounts ──────────────────────────────────────────────────

export function getAllFbAccounts() {
  return getDB().fbAccounts || [];
}

export function addFbAccount(data) {
  const db = getDB();
  if (!db.fbAccounts) db.fbAccounts = [];
  const acc = { id: `fba_${Date.now()}`, ...data };
  db.fbAccounts.push(acc);
  saveDB(db, 'fbAccounts');
  return acc;
}

export function updateFbAccount(id, updates) {
  const db = getDB();
  if (!db.fbAccounts) return null;
  const i = db.fbAccounts.findIndex((a) => a.id === id);
  if (i !== -1) {
    db.fbAccounts[i] = { ...db.fbAccounts[i], ...updates };
    saveDB(db, 'fbAccounts');
    return db.fbAccounts[i];
  }
  return null;
}

export function deleteFbAccount(id) {
  const db = getDB();
  if (!db.fbAccounts) return;
  db.fbAccounts = db.fbAccounts.filter((a) => a.id !== id);
  saveDB(db, 'fbAccounts');
}

// ── Expenses ─────────────────────────────────────────────────────

export function getAllExpenses() {
  const db = getDB();
  const list = (db.expenses || []).slice();
  list.sort((a, b) => b.date.localeCompare(a.date));
  return list;
}

async function expenseRequest(method, body) {
  const response = await fetch('/api/data', {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || `Не удалось сохранить расход (${response.status})`);
  }
  return response.json();
}

export async function addExpense(data) {
  const db = getDB();
  const exp = { id: `exp_${Date.now()}`, ...data, amount: Number(data.amount) };
  const result = await expenseRequest('POST', { collection: 'expenses', rows: [exp] });
  if (!db.expenses) db.expenses = [];
  db.expenses.push(result.expense);
  return result.expense;
}

export async function updateExpense(storageId, updates) {
  const db = getDB();
  const result = await expenseRequest('PATCH', { collection: 'expenses', storageId, changes: updates });
  const index = (db.expenses || []).findIndex((expense) => expense._storageId === storageId);
  if (index !== -1) db.expenses[index] = result.expense;
  return result.expense;
}

export async function deleteExpense(storageId) {
  const db = getDB();
  await expenseRequest('DELETE', { collection: 'expenses', storageId });
  db.expenses = (db.expenses || []).filter((expense) => expense._storageId !== storageId);
}

// ── Conversions ──────────────────────────────────────────────────

/**
 * Returns conversions filtered by date range (YYYY-MM-DD prefix match on conversionTime).
 */
function utcTimestamp(date = new Date()) {
  return date.toISOString().slice(0, 19).replace('T', ' ');
}

export function getConversionsByRange(from, to, now = new Date()) {
  const db = getDB();
  const convs = db.conversions || [];
  return convs.filter((c) => {
    const day = c.warsawDate || (c.conversionTime ? c.conversionTime.slice(0, 10) : '');
    return day >= from && day <= to && (!c.conversionTime || c.conversionTime <= utcTimestamp(now));
  });
}

/** Read-only snapshot used by preview/reporting tools. */
export function getAllConversions() {
  const db = getDB();
  return [...(db.conversions || [])];
}

export async function importConversions(rows) {
  const cleanRows = rows.map(({ pairId, kind, ...row }) => row);
  const response = await fetch('/api/data', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ collection: 'conversions', rows: cleanRows }),
  });
  if (!response.ok) throw new Error(`Не удалось импортировать конверсии (${response.status})`);
  const result = await response.json();
  const existingIds = new Set((database.conversions || []).map((row) => row.id));
  database.conversions = [...(database.conversions || []), ...cleanRows.filter((row) => !existingIds.has(row.id))];
  return result;
}

export async function updateConversion(id, changes) {
  const response = await fetch('/api/data', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ collection: 'conversions', id, changes }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || `Не удалось обновить конверсию (${response.status})`);
  }
  const result = await response.json();
  const index = (database.conversions || []).findIndex((row) => row.id === id);
  if (index !== -1) database.conversions[index] = result.conversion;
  return result.conversion;
}

export async function deleteConversion(id) {
  const response = await fetch('/api/data', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ collection: 'conversions', id }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || `Не удалось удалить конверсию (${response.status})`);
  }
  database.conversions = (database.conversions || []).filter((row) => row.id !== id);
  return response.json();
}

/** Standard campaign name for filters/display, merging OCR-truncated duplicates. */
export function getCanonicalCampaign(campaign, conversions) {
  const db = conversions ? null : getDB();
  const convs = conversions || db.conversions || [];
  const canonicalMap = buildCampaignCanonicalMap(convs);
  const raw = String(campaign || '').trim();

  return canonicalMap.get(raw) || raw;
}

export function getCampaignCanonicalMap(conversions) {
  const db = conversions ? null : getDB();
  const convs = conversions || db.conversions || [];

  return buildCampaignCanonicalMap(convs);
}

/** Unique standardized campaign names from all conversions */
export function getConversionCampaigns() {
  const db = getDB();
  const convs = db.conversions || [];
  const canonicalMap = buildCampaignCanonicalMap(convs);
  const counts = new Map();

  convs.forEach((c) => {
    const raw = String(c.campaign || '').trim();
    if (!raw) return;

    const canonical = canonicalMap.get(raw) || raw;
    counts.set(canonical, (counts.get(canonical) || 0) + 1);
  });

  return [...counts.keys()].sort((a, b) => counts.get(b) - counts.get(a) || a.localeCompare(b));
}

/**
 * Calculates profit stats for a given date range.
 * Revenue = sum of conversions with origStatus === 'sale'
 * Expenses = sum of all expense records in range
 * Profit   = Revenue − Expenses
 * ROI      = Profit / Expenses × 100
 */
export function getProfitStats(from, to) {
  const db = getDB();

  const salesConvs = (db.conversions || []).filter((c) => {
    const day = c.conversionTime ? c.conversionTime.slice(0, 10) : '';
    return day >= from && day <= to && c.origStatus === 'sale';
  });
  const revenue = round2(salesConvs.reduce((s, c) => s + (Number(c.revenue) || 0), 0));

  const periodExpenses = (db.expenses || []).filter((e) => e.date >= from && e.date <= to);
  const expenses = round2(periodExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0));

  const profit = round2(revenue - expenses);
  const roi    = expenses > 0 ? round2((profit / expenses) * 100) : 0;

  return {
    revenue,
    expenses,
    profit,
    roi,
    salesCount: salesConvs.length,
    expenseRows: periodExpenses,
  };
}
